/** @format */

import React, { useState, useMemo } from 'react';
import { Table, Input, Button, Space, Popconfirm, Spin, InputNumber } from 'antd';
import { DeleteOutlined, ReloadOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { ListedLead, ListedLeadFilterInput } from '../types/listedLeads.types';
import * as listedLeadService from '../services/listedLeadsService';
import { toast } from 'react-toastify';

interface ListedLeadsTableProps {
  listedLeads: ListedLead[];
  loading: boolean;
  onRefresh: () => Promise<void>;
  onEditComplete?: () => Promise<void>;
  filter?: ListedLeadFilterInput;
}

interface EditingListedLead {
  id: string;
  fields: {
    listId?: string;
    listName?: string;
  };
}

/**
 * Listed Leads Table Component
 * Displays listed leads with search, filter, sort, inline edit, and delete functionality
 */
export const ListedLeadsTable: React.FC<ListedLeadsTableProps> = ({
  listedLeads,
  loading,
  onRefresh,
  onEditComplete,
  filter,
}) => {
  const [searchText, setSearchText] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditingListedLead | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  const filteredListedLeads = useMemo(() => {
    return listedLeads.filter(
      (listedLead) =>
        (listedLead.listId?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
        (listedLead.listName?.toLowerCase() || '').includes(searchText.toLowerCase())
    );
  }, [listedLeads, searchText]);

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      await listedLeadService.deleteListedLead(id);
      toast.success('Listed lead deleted successfully');
      await onRefresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete listed lead');
    } finally {
      setDeleting(null);
    }
  };

  const handleEditStart = (id: string, listedLead: ListedLead) => {
    setEditing({
      id,
      fields: {
        listId: listedLead.listId,
        listName: listedLead.listName,
      },
    });
  };

  const handleFieldChange = (field: string, value: any) => {
    if (!editing) return;
    setEditing({
      ...editing,
      fields: {
        ...editing.fields,
        [field]: value,
      },
    });
  };

  const handleEditSave = async (id: string) => {
    if (!editing || editing.id !== id) return;

    setUpdating(id);
    try {
      const updateData: any = {};
      if (editing.fields.listId !== undefined) updateData.listId = editing.fields.listId;
      if (editing.fields.listName !== undefined) updateData.listName = editing.fields.listName;

      await listedLeadService.updateListedLead(id, updateData);
      toast.success('Listed lead updated successfully');
      await onEditComplete?.();
      setEditing(null);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update listed lead');
    } finally {
      setUpdating(null);
    }
  };

  const handleEditCancel = () => {
    setEditing(null);
  };

  const columns = [
    {
      title: 'List ID',
      dataIndex: 'listId',
      key: 'listId',
      width: 150,
      sorter: (a: ListedLead, b: ListedLead) => (a.listId || '').localeCompare(b.listId || ''),
      render: (listId: string | undefined, record: ListedLead) => {
        const isEditing = editing?.id === record._id;
        return isEditing ? (
          <Input
            value={editing.fields.listId}
            onChange={(e) => handleFieldChange('listId', e.target.value)}
            placeholder='Enter list ID'
            autoFocus
            style={{ width: '100%' }}
          />
        ) : (
          <span onDoubleClick={() => handleEditStart(record._id, record)}>
            {listId || '-'}
          </span>
        );
      },
    },
    {
      title: 'List Name',
      dataIndex: 'listName',
      key: 'listName',
      width: 200,
      sorter: (a: ListedLead, b: ListedLead) => (a.listName || '').localeCompare(b.listName || ''),
      render: (listName: string | undefined, record: ListedLead) => {
        const isEditing = editing?.id === record._id;
        return isEditing ? (
          <Input
            value={editing.fields.listName}
            onChange={(e) => handleFieldChange('listName', e.target.value)}
            placeholder='Enter list name'
            autoFocus
            style={{ width: '100%' }}
          />
        ) : (
          <span onDoubleClick={() => handleEditStart(record._id, record)}>
            {listName || '-'}
          </span>
        );
      },
    },
    {
      title: 'Office ID',
      dataIndex: 'officeId',
      key: 'officeId',
      width: 150,
      sorter: (a: ListedLead, b: ListedLead) => (a.officeId || '').localeCompare(b.officeId || ''),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      sorter: (a: ListedLead, b: ListedLead) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (createdAt: string) => {
        if (!createdAt) return 'N/A';
        return new Date(createdAt).toLocaleString();
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_: any, record: ListedLead) => {
        const isEditing = editing?.id === record._id;
        return (
          <Space size='small'>
            {isEditing ? (
              <>
                <Button
                  type='text'
                  size='small'
                  icon={<CheckOutlined />}
                  onClick={() => handleEditSave(record._id)}
                  loading={updating === record._id}
                />
                <Button
                  type='text'
                  size='small'
                  icon={<CloseOutlined />}
                  onClick={handleEditCancel}
                  danger
                />
              </>
            ) : (
              <Button
                type='text'
                size='small'
                icon={<EditOutlined />}
                onClick={() => handleEditStart(record._id, record)}
                title='Click to edit all fields, or double-click any field'
              />
            )}
            <Popconfirm
              title='Delete Listed Lead'
              description='Are you sure you want to delete this listed lead?'
              onConfirm={() => handleDelete(record._id)}
              okText='Yes'
              cancelText='No'
              disabled={deleting === record._id}
            >
              <Button
                type='text'
                danger
                size='small'
                icon={<DeleteOutlined />}
                loading={deleting === record._id}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Input
          placeholder='Search by List ID or List Name...'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: '400px' }}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={onRefresh}
          loading={loading}
          type='primary'
        >
          Refresh
        </Button>
      </div>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={filteredListedLeads}
          rowKey='_id'
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: [10, 25, 50, 100],
            showTotal: (total) => `Total ${total} listed leads`,
          }}
          size='small'
        />
      </Spin>
    </div>
  );
};

export default ListedLeadsTable;
